/**
 * The user's navigation arrangement, in the store.
 *
 * It lives here rather than in a hook's `useState` for one reason: two screens
 * read it. The sidebar draws the tree; Customize navigation rearranges it. With
 * the state inside a hook, each of them got a private copy — saving on the
 * settings page wrote localStorage and updated that page's copy, while the
 * sidebar went on rendering the old order until the app was reloaded. Anything
 * two components share has to live somewhere they both point at, and this app
 * already has exactly one such place.
 *
 * `persist` is deliberately async even though localStorage is not. It is the
 * seam a real preferences endpoint drops into, and it means `saving` and
 * `error` are already wired to a promise rather than to a synchronous call that
 * would have to be rewritten the day the API exists.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  EMPTY_ORDER,
  navStorageKey,
  readNavOrder,
  writeNavOrder,
  type NavOrder,
} from '../../components/layout/use-nav-order';

interface NavState {
  order: NavOrder;
  /** Whose arrangement `order` holds, so a user change can re-read it. */
  userId: string;
  saving: boolean;
  error: string | null;
}

const initialState: NavState = {
  order: EMPTY_ORDER,
  userId: '',
  saving: false,
  error: null,
};

/** Write the arrangement, then hand it back for the reducer to hold. */
export const saveNavOrder = createAsyncThunk(
  'nav/save',
  async ({ userId, order }: { userId: string; order: NavOrder }, { rejectWithValue }) => {
    try {
      writeNavOrder(userId, order);
      return { userId, order };
    } catch (e) {
      return rejectWithValue(
        e instanceof Error ? e.message : 'Could not save your navigation arrangement.'
      );
    }
  }
);

const navSlice = createSlice({
  name: 'nav',
  initialState,
  reducers: {
    /** Load this user's saved arrangement out of storage into the store. */
    hydrateNavOrder(state, action: { payload: string }) {
      const userId = action.payload;
      if (state.userId === userId) return;
      state.userId = userId;
      state.order = readNavOrder(userId);
      state.error = null;
    },
    /** Back to the order the taxonomy declares. */
    resetNavOrder(state) {
      state.order = EMPTY_ORDER;
      state.error = null;
      try {
        localStorage.removeItem(navStorageKey(state.userId));
      } catch {
        /* storage refused; the reset still applies for this session */
      }
    },
    clearNavError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(saveNavOrder.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveNavOrder.fulfilled, (state, action) => {
        state.saving = false;
        state.userId = action.payload.userId;
        // The sidebar selects this, so it re-renders the moment this lands —
        // which is the whole point of moving the state here.
        state.order = action.payload.order;
      })
      .addCase(saveNavOrder.rejected, (state, action) => {
        state.saving = false;
        state.error =
          (action.payload as string) || 'Could not save your navigation arrangement.';
        // `order` is left alone on purpose: the draft the user built is still
        // on screen, so they can fix the problem and press Save again.
      });
  },
});

export const { hydrateNavOrder, resetNavOrder, clearNavError } = navSlice.actions;
export default navSlice.reducer;
